import { create } from 'zustand';

interface RowEditState {
  localPrice: number | null;
  localDiscount: number | null;
  originalPrice: number | null;
  originalDiscount: number | null;
  localOfferGroupId: string | null;
  localOfferGroupName: string | null;
  originalOfferGroupId: string | null;
  originalOfferGroupName: string | null;
  offerGroupRevision: number;
}

interface CommitRowOptions {
  offerGroupId?: string | null;
  offerGroupName?: string | null;
  offerGroupRevision?: number;
}

interface OfferInlineEditStore {
  rows: Record<string, RowEditState>;
  initRow: (productId: string, price: number | null, discount: number | null, offerGroupId?: string | null, offerGroupName?: string | null) => void;
  setPrice: (productId: string, price: number | null) => void;
  setDiscount: (productId: string, discount: number | null) => void;
  setOfferGroup: (productId: string, offerGroupId: string | null, offerGroupName: string | null) => void;
  commitRow: (productId: string, options?: CommitRowOptions) => void;
  rollbackRow: (productId: string) => void;
  commitOfferGroup: (productId: string) => void;
  rollbackOfferGroup: (productId: string) => void;
  removeRows: (productIds: string[]) => void;
}

export const useOfferInlineEditStore = create<OfferInlineEditStore>((set) => ({
  rows: {},
  initRow: (productId, price, discount, offerGroupId = null, offerGroupName = null) =>
    set((state) => {
      if (state.rows[productId] !== undefined) return state;
      return {
        rows: {
          ...state.rows,
          [productId]: {
            localPrice: price,
            localDiscount: discount,
            originalPrice: price,
            originalDiscount: discount,
            localOfferGroupId: offerGroupId,
            localOfferGroupName: offerGroupName,
            originalOfferGroupId: offerGroupId,
            originalOfferGroupName: offerGroupName,
            offerGroupRevision: 0,
          },
        },
      };
    }),
  setPrice: (productId, price) =>
    set((state) => ({
      rows: { ...state.rows, [productId]: { ...state.rows[productId], localPrice: price } },
    })),
  setDiscount: (productId, discount) =>
    set((state) => ({
      rows: { ...state.rows, [productId]: { ...state.rows[productId], localDiscount: discount } },
    })),
  setOfferGroup: (productId, offerGroupId, offerGroupName) =>
    set((state) => ({
      rows: {
        ...state.rows,
        [productId]: {
          ...state.rows[productId],
          localOfferGroupId: offerGroupId,
          localOfferGroupName: offerGroupName,
          offerGroupRevision: (state.rows[productId]?.offerGroupRevision ?? 0) + 1,
        },
      },
    })),
  commitRow: (productId, options) =>
    set((state) => {
      const row = state.rows[productId];
      if (!row) return state;
      const shouldCommitOfferGroup =
        options?.offerGroupRevision !== undefined &&
        options.offerGroupRevision === row.offerGroupRevision;

      return {
        rows: {
          ...state.rows,
          [productId]: {
            ...row,
            originalPrice: row.localPrice,
            originalDiscount: row.localDiscount,
            ...(shouldCommitOfferGroup && {
              localOfferGroupId: options.offerGroupId ?? null,
              localOfferGroupName: options.offerGroupName ?? null,
              originalOfferGroupId: options.offerGroupId ?? null,
              originalOfferGroupName: options.offerGroupName ?? null,
            }),
          },
        },
      };
    }),
  rollbackRow: (productId) =>
    set((state) => {
      const row = state.rows[productId];
      if (!row) return state;
      return {
        rows: {
          ...state.rows,
          [productId]: {
            ...row,
            localPrice: row.originalPrice,
            localDiscount: row.originalDiscount,
          },
        },
      };
    }),
  commitOfferGroup: (productId) =>
    set((state) => {
      const row = state.rows[productId];
      if (!row) return state;
      return {
        rows: {
          ...state.rows,
          [productId]: {
            ...row,
            originalOfferGroupId: row.localOfferGroupId,
            originalOfferGroupName: row.localOfferGroupName,
          },
        },
      };
    }),
  rollbackOfferGroup: (productId) =>
    set((state) => {
      const row = state.rows[productId];
      if (!row) return state;
      return {
        rows: {
          ...state.rows,
          [productId]: {
            ...row,
            localOfferGroupId: row.originalOfferGroupId,
            localOfferGroupName: row.originalOfferGroupName,
          },
        },
      };
    }),
  removeRows: (productIds) =>
    set((state) => {
      const newRows = { ...state.rows };
      productIds.forEach((id) => delete newRows[id]);
      return { rows: newRows };
    }),
}));
